export const IMPACT_MODEL_VERSION = 'v1' as const

export const IMPACT_MODEL_V1_ASSUMPTIONS = {
  loadedHourlyRate: 150,
  preparationHoursPerInterviewer: 0.75,
  evaluationHoursPerInterviewer: 0.75,
  hiringKitPreparationSavingsHoursPerInterviewer: 0.5,
  potentialCopilotSavingsHoursPerInterviewer: 0.5,
  representativeStageDurationHours: 1,
  representativeStageInterviewers: 2,
  candidateAdvancementRate: 0.5,
} as const

export type ImpactDecisionMethod =
  | 'first_past_the_post'
  | 'cohort_comparison'
  | 'threshold_based_hybrid'
  | 'time_boxed'
  | string

export interface ImpactModelInputsV1 {
  initialScreenCandidates: number
  comparableRoleStages: number
  plannedHires: number
  desiredFinalists?: number
  decisionMethod?: ImpactDecisionMethod
}

export interface ImpactModelInterviewerV1 {
  name?: string
  isBackup?: boolean
}

export interface ImpactModelStageV1 {
  id?: string
  name?: string
  duration?: number
  daysToNext?: number
  interviewers?: ImpactModelInterviewerV1[]
}

export interface ImpactModelStageResultV1 {
  stageIndex: number
  stageName: string
  expectedCandidates: number
  requiredInterviewers: number
  interviewHours: number
  surroundingWorkHours: number
  grossHiringTeamHours: number
  grossHiringTeamCost: number
  hiringKitSavingsHours: number
  hiringKitSavingsCost: number
  plannedHiringTeamHours: number
  plannedHiringTeamCost: number
}

export interface ImpactModelResultV1 {
  modelVersion: typeof IMPACT_MODEL_VERSION
  assumptions: typeof IMPACT_MODEL_V1_ASSUMPTIONS
  candidateFloor: number
  plannedStageCandidates: number[]
  comparableProcessStageCandidates: number[]
  plannedProcessDays: number
  candidateInterviewHours: number
  totalCandidateInterviewHours: number
  grossHiringTeamHours: number
  grossHiringTeamCost: number
  plannedHiringTeamHours: number
  plannedHiringTeamCost: number
  hiringKitPreparationSavingsHours: number
  hiringKitPreparationSavingsCost: number
  stageReductionCount: number
  stageReductionSavingsHours: number
  stageReductionSavingsCost: number
  additionalStageCount: number
  additionalStageInvestmentHours: number
  additionalStageInvestmentCost: number
  potentialCopilotSavingsHours: number
  potentialCopilotSavingsCost: number
  estimatedTimeSavingsHours: number
  estimatedCostSavings: number
  stages: ImpactModelStageResultV1[]
}

export interface ImpactModelSnapshotV1 {
  modelVersion: typeof IMPACT_MODEL_VERSION
  calculatedAt: string
  inputs: ImpactModelInputsV1
  stages: ImpactModelStageV1[]
  assumptions: typeof IMPACT_MODEL_V1_ASSUMPTIONS
  result: ImpactModelResultV1
}

export interface ImpactRollupEntryV1 {
  status?: string
  openStatus?: string
  result?: string
  isExample?: boolean
  completedAt?: string
  snapshot?: ImpactModelSnapshotV1
}

export interface ImpactRollupV1 {
  qualifyingHiringKits: number
  estimatedTimeSavingsHours: number
  estimatedCostSavings: number
}

function positiveWholeNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return Math.floor(parsed)
}

function nonNegativeNumber(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

export function validateImpactModelInputsV1(
  inputs: Partial<ImpactModelInputsV1> | undefined,
): string[] {
  if (!inputs) return ['Impact estimates are required.']

  const errors: string[] = []
  const initialCandidates = positiveWholeNumber(inputs.initialScreenCandidates)
  const comparableStages = positiveWholeNumber(inputs.comparableRoleStages)
  const plannedHires = positiveWholeNumber(inputs.plannedHires)
  const desiredFinalists = positiveWholeNumber(inputs.desiredFinalists)

  if (!initialCandidates)
    errors.push('Enter the expected number of initial interviews.')
  if (!comparableStages)
    errors.push('Enter the usual number of interview stages.')
  if (!plannedHires) errors.push('Enter the number of people you are hiring.')
  if (initialCandidates && plannedHires && initialCandidates < plannedHires) {
    errors.push('Initial interviews cannot be fewer than planned hires.')
  }

  if (inputs.decisionMethod === 'cohort_comparison') {
    if (!desiredFinalists) {
      errors.push('Enter the number of finalists you want to compare.')
    } else {
      if (plannedHires && desiredFinalists < plannedHires) {
        errors.push('Finalists cannot be fewer than planned hires.')
      }
      if (initialCandidates && desiredFinalists > initialCandidates) {
        errors.push('Finalists cannot exceed initial interviews.')
      }
    }
  }

  return errors
}

export function candidateCountAtStageV1(
  initialCandidates: number,
  stageIndex: number,
  candidateFloor: number,
): number {
  return Math.max(
    positiveWholeNumber(candidateFloor, 1),
    Math.ceil(
      positiveWholeNumber(initialCandidates, 1) *
        Math.pow(
          IMPACT_MODEL_V1_ASSUMPTIONS.candidateAdvancementRate,
          Math.max(0, stageIndex),
        ),
    ),
  )
}

export function calculateImpactModelV1(
  inputs: ImpactModelInputsV1,
  stages: ImpactModelStageV1[],
): ImpactModelResultV1 {
  const errors = validateImpactModelInputsV1(inputs)
  if (errors.length > 0) throw new Error(errors.join(' '))

  const initialCandidates = positiveWholeNumber(inputs.initialScreenCandidates)
  const comparableStages = positiveWholeNumber(inputs.comparableRoleStages)
  const plannedHires = positiveWholeNumber(inputs.plannedHires)
  const desiredFinalists = positiveWholeNumber(inputs.desiredFinalists)
  const candidateFloor =
    inputs.decisionMethod === 'cohort_comparison'
      ? Math.max(plannedHires, desiredFinalists)
      : plannedHires

  const plannedStageCandidates = stages.map((_, index) =>
    candidateCountAtStageV1(initialCandidates, index, candidateFloor),
  )
  const comparableProcessStageCandidates = Array.from(
    { length: comparableStages },
    (_, index) =>
      candidateCountAtStageV1(initialCandidates, index, candidateFloor),
  )

  const stageResults: ImpactModelStageResultV1[] = stages.map(
    (stage, stageIndex) => {
      const expectedCandidates = plannedStageCandidates[stageIndex]
      const requiredInterviewers = (stage.interviewers ?? []).filter(
        (interviewer) => !interviewer.isBackup,
      ).length
      const interviewDurationHours = nonNegativeNumber(stage.duration) / 60
      const interviewHours =
        expectedCandidates * requiredInterviewers * interviewDurationHours
      const surroundingWorkHours =
        expectedCandidates *
        requiredInterviewers *
        (IMPACT_MODEL_V1_ASSUMPTIONS.preparationHoursPerInterviewer +
          IMPACT_MODEL_V1_ASSUMPTIONS.evaluationHoursPerInterviewer)
      const grossHiringTeamHours = interviewHours + surroundingWorkHours
      const hiringKitSavingsHours =
        expectedCandidates *
        requiredInterviewers *
        IMPACT_MODEL_V1_ASSUMPTIONS.hiringKitPreparationSavingsHoursPerInterviewer
      const plannedHiringTeamHours =
        grossHiringTeamHours - hiringKitSavingsHours

      return {
        stageIndex,
        stageName: stage.name || `Stage ${stageIndex + 1}`,
        expectedCandidates,
        requiredInterviewers,
        interviewHours,
        surroundingWorkHours,
        grossHiringTeamHours,
        grossHiringTeamCost:
          grossHiringTeamHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
        hiringKitSavingsHours,
        hiringKitSavingsCost:
          hiringKitSavingsHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
        plannedHiringTeamHours,
        plannedHiringTeamCost:
          plannedHiringTeamHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
      }
    },
  )

  const grossHiringTeamHours = stageResults.reduce(
    (total, stage) => total + stage.grossHiringTeamHours,
    0,
  )
  const hiringKitPreparationSavingsHours = stageResults.reduce(
    (total, stage) => total + stage.hiringKitSavingsHours,
    0,
  )
  const plannedHiringTeamHours = stageResults.reduce(
    (total, stage) => total + stage.plannedHiringTeamHours,
    0,
  )
  const potentialCopilotSavingsHours = stageResults.reduce(
    (total, stage) =>
      total +
      stage.expectedCandidates *
        stage.requiredInterviewers *
        IMPACT_MODEL_V1_ASSUMPTIONS.potentialCopilotSavingsHoursPerInterviewer,
    0,
  )

  const stageReductionCount = Math.max(0, comparableStages - stages.length)
  // The former process is known only by stage count. To remain conservative,
  // reduced stages are treated as later-stage steps in its candidate funnel.
  const reducedStageCandidateCounts = comparableProcessStageCandidates.slice(
    stages.length,
  )
  const representativeHoursPerCandidate =
    (IMPACT_MODEL_V1_ASSUMPTIONS.representativeStageDurationHours +
      IMPACT_MODEL_V1_ASSUMPTIONS.preparationHoursPerInterviewer +
      IMPACT_MODEL_V1_ASSUMPTIONS.evaluationHoursPerInterviewer) *
    IMPACT_MODEL_V1_ASSUMPTIONS.representativeStageInterviewers
  const stageReductionSavingsHours = reducedStageCandidateCounts.reduce(
    (total, candidates) => total + candidates * representativeHoursPerCandidate,
    0,
  )

  const additionalStageCount = Math.max(0, stages.length - comparableStages)
  const additionalStageInvestmentHours =
    additionalStageCount > 0
      ? stageResults
          .slice(comparableStages)
          .reduce((total, stage) => total + stage.plannedHiringTeamHours, 0)
      : 0

  const plannedProcessDays = stages.reduce(
    (total, stage, stageIndex) =>
      stageIndex === stages.length - 1
        ? total
        : total + nonNegativeNumber(stage.daysToNext),
    0,
  )
  const candidateInterviewHours = stages.reduce(
    (total, stage) => total + nonNegativeNumber(stage.duration) / 60,
    0,
  )
  const totalCandidateInterviewHours = stages.reduce(
    (total, stage, stageIndex) =>
      total +
      (nonNegativeNumber(stage.duration) / 60) *
        plannedStageCandidates[stageIndex],
    0,
  )
  const estimatedTimeSavingsHours =
    stageReductionSavingsHours + hiringKitPreparationSavingsHours

  return {
    modelVersion: IMPACT_MODEL_VERSION,
    assumptions: IMPACT_MODEL_V1_ASSUMPTIONS,
    candidateFloor,
    plannedStageCandidates,
    comparableProcessStageCandidates,
    plannedProcessDays,
    candidateInterviewHours,
    totalCandidateInterviewHours,
    grossHiringTeamHours,
    grossHiringTeamCost:
      grossHiringTeamHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    plannedHiringTeamHours,
    plannedHiringTeamCost:
      plannedHiringTeamHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    hiringKitPreparationSavingsHours,
    hiringKitPreparationSavingsCost:
      hiringKitPreparationSavingsHours *
      IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    stageReductionCount,
    stageReductionSavingsHours,
    stageReductionSavingsCost:
      stageReductionSavingsHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    additionalStageCount,
    additionalStageInvestmentHours,
    additionalStageInvestmentCost:
      additionalStageInvestmentHours *
      IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    potentialCopilotSavingsHours,
    potentialCopilotSavingsCost:
      potentialCopilotSavingsHours *
      IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    estimatedTimeSavingsHours,
    estimatedCostSavings:
      estimatedTimeSavingsHours * IMPACT_MODEL_V1_ASSUMPTIONS.loadedHourlyRate,
    stages: stageResults,
  }
}

export function createImpactModelSnapshotV1(
  inputs: ImpactModelInputsV1,
  stages: ImpactModelStageV1[],
  calculatedAt = new Date().toISOString(),
): ImpactModelSnapshotV1 {
  // Role-plan stages can be Vue reactive proxies. Serialize the persisted
  // JSON data rather than passing those proxies to structuredClone.
  const cleanInputs: ImpactModelInputsV1 = JSON.parse(JSON.stringify(inputs))
  const cleanStages: ImpactModelStageV1[] = JSON.parse(JSON.stringify(stages))
  return {
    modelVersion: IMPACT_MODEL_VERSION,
    calculatedAt,
    inputs: cleanInputs,
    stages: cleanStages,
    assumptions: IMPACT_MODEL_V1_ASSUMPTIONS,
    result: calculateImpactModelV1(cleanInputs, cleanStages),
  }
}

export function roundImpactHoursV1(hours: number): number {
  return Math.round(hours)
}

export function roundImpactCostV1(cost: number): number {
  return Math.round(cost / 100) * 100
}

export function calculateImpactRollupV1(
  entries: ImpactRollupEntryV1[],
  now = new Date(),
): ImpactRollupV1 {
  const twelveMonthsAgo = new Date(now)
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1)

  const qualifying = entries.filter((entry) => {
    if (entry.status !== 'complete' || entry.isExample) return false
    if (entry.openStatus === 'archived') return false
    if (entry.openStatus === 'closed' && entry.result !== 'filled') return false
    if (entry.snapshot?.modelVersion !== IMPACT_MODEL_VERSION) return false
    const completedAt = new Date(entry.completedAt ?? '')
    return (
      Number.isFinite(completedAt.getTime()) &&
      completedAt >= twelveMonthsAgo &&
      completedAt <= now
    )
  })

  const estimatedTimeSavingsHours = qualifying.reduce(
    (total, entry) =>
      total + (entry.snapshot?.result.estimatedTimeSavingsHours ?? 0),
    0,
  )
  const estimatedCostSavings = qualifying.reduce(
    (total, entry) =>
      total + (entry.snapshot?.result.estimatedCostSavings ?? 0),
    0,
  )

  return {
    qualifyingHiringKits: qualifying.length,
    estimatedTimeSavingsHours: roundImpactHoursV1(estimatedTimeSavingsHours),
    estimatedCostSavings: roundImpactCostV1(estimatedCostSavings),
  }
}
